/**
 * EstadoChip — Chip de estado consistente para reservaciones.
 * Estados del backend: pendiente, confirmada, completada, cancelada, hold, no_show, suspendida
 *
 * Comportamiento dinámico (opción B acordada con el operador):
 * Cuando el estado indica compromiso firme (confirmada / hold) PERO hay
 * saldo pendiente por cobrar, el chip se pinta AMARILLO con el monto,
 * para que el operador vea de un vistazo que aún falta cobrar. Sin esto,
 * un "Confirmada" azul se lee como "todo OK" y se puede confundir con
 * una reserva realmente pagada.
 */
import React from 'react';
import { Chip, Tooltip } from '@mui/material';

const CONFIG = {
  pendiente:  { bg: '#fef3c7', color: '#92400e', label: 'Pendiente' },
  confirmada: { bg: '#dbeafe', color: '#1e40af', label: 'Confirmada' },
  hold:       { bg: '#fae8ff', color: '#86198f', label: 'Hold' },
  completada: { bg: '#dcfce7', color: '#166534', label: 'Completada' },
  cancelada:  { bg: '#fee2e2', color: '#991b1b', label: 'Cancelada' },
  no_show:    { bg: '#fef2f2', color: '#7f1d1d', label: 'No-show' },
  suspendida: { bg: '#fef9c3', color: '#854d0e', label: 'Suspendida' },
  embarcada:  { bg: '#ccfbf1', color: '#0f766e', label: 'Embarcada' },
};

// Amarillo "atención" — mismo tono que usa el muelle para "debe $X".
// Estados en los que un saldo > 0 vira el chip: confirmada y hold.
// (Otros estados no aplican: pendiente ya es amarillo, cancelada/no_show/completada
// no deberían tener saldo pendiente relevante.)
const CONFIG_CON_SALDO = { bg: '#fef3c7', color: '#92400e' };
const _fmtMoneda = (n) => '$' + new Intl.NumberFormat('es-CO', { minimumFractionDigits: 0 }).format(Math.round(Number(n) || 0));

export default function EstadoChip({
  estado, saldoPendiente = null, saldoMuelleMonto = null, cxcAgenciaMonto = null,
  size = 'small',
}) {
  const base = CONFIG[estado] || { bg: '#e2e8f0', color: '#475569', label: estado || '—' };
  const saldo = Number(saldoPendiente || 0);
  const debeCobrar = saldo > 0 && (estado === 'confirmada' || estado === 'hold');

  // El total es cierto —la reserva sí debe esa plata— pero cuando el saldo
  // está repartido entre el muelle y la agencia, ese número solo no dice
  // cuánto se cobra dónde. El chip muestra la parte del MUELLE, que es la
  // que alguien va a cobrar hoy, y el desglose completo va en el tooltip.
  const mu = Number(saldoMuelleMonto || 0);
  const cx = Number(cxcAgenciaMonto || 0);
  const partido = debeCobrar && mu > 0 && cx > 0;

  const cfg = debeCobrar
    ? {
      ...CONFIG_CON_SALDO,
      label: partido
        ? `${base.label} · muelle ${_fmtMoneda(mu)} + CxC ${_fmtMoneda(cx)}`
        : `${base.label} · debe ${_fmtMoneda(saldo)}`,
    }
    : base;

  const chip = (
    <Chip size={size} label={cfg.label}
      sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 700, fontSize: 11, height: 22 }} />
  );

  if (!partido) return chip;
  return (
    <Tooltip arrow title={
      `Debe ${_fmtMoneda(saldo)} en total: ${_fmtMoneda(mu)} se cobran al `
      + `pasajero en el muelle y ${_fmtMoneda(cx)} quedan como cuenta por `
      + 'cobrar a la agencia.'
    }>
      {chip}
    </Tooltip>
  );
}

export const ESTADOS = Object.keys(CONFIG);
export const ESTADO_LABELS = CONFIG;
